import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, LayoutGrid, Table as TableIcon, ExternalLink, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const COLUMNS = ["Pending", "In Progress", "Review", "Approved", "Changes Requested"];

export default function Tasks() {
  const { effectiveRole } = useAuth();
  const [view, setView] = useState("kanban"); // kanban | table
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [editors, setEditors] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openAdd, setOpenAdd] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const [feedback, setFeedback] = useState("");

  const [form, setForm] = useState({
    client_id: "",
    editor_id: "",
    title: "",
    deadline: "",
    priority: "Medium",
    raw_asset_url: "",
  });

  const load = async () => {
    const [t, c, e] = await Promise.all([
      api.get("/tasks"),
      api.get("/clients"),
      api.get("/editors"),
    ]);
    setTasks(t.data);
    setClients(c.data);
    setEditors(e.data);
  };
  useEffect(() => { load(); }, []);

  const cliName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const edName = (id) => editors.find((e) => e.user_id === id)?.name || "—";
  const edPic = (id) => editors.find((e) => e.user_id === id)?.picture;

  const filtered = useMemo(() => {
    if (filter === "all") return tasks;
    return tasks.filter((t) => t.status === filter);
  }, [tasks, filter]);

  const submitAdd = async () => {
    try {
      await api.post("/tasks", form);
      toast.success("Task assigned", { description: "Editor's dashboard will refresh with the new task." });
      setOpenAdd(false);
      setForm({ client_id: "", editor_id: "", title: "", deadline: "", priority: "Medium", raw_asset_url: "" });
      load();
    } catch { toast.error("Could not assign task"); }
  };

  const approve = async (id) => {
    await api.post(`/tasks/${id}/approve`);
    toast.success("Approved & filed", { description: "Approval email queued for client delivery." });
    setOpenDetail(null);
    load();
  };

  const requestChanges = async (id) => {
    if (!feedback.trim()) { toast.error("Add feedback notes first"); return; }
    await api.post(`/tasks/${id}/request-changes`, { feedback_notes: feedback });
    toast.success("Sent back to editor");
    setFeedback("");
    setOpenDetail(null);
    load();
  };

  // Drag & drop between columns
  const onDragStart = (e, id) => e.dataTransfer.setData("task_id", id);
  const onDrop = async (e, status) => {
    e.preventDefault();
    e.currentTarget.classList.remove("kanban-col-drag");
    const id = e.dataTransfer.getData("task_id");
    if (!id) return;
    await api.patch(`/tasks/${id}`, { status });
    load();
  };
  const onDragOver = (e) => { e.preventDefault(); e.currentTarget.classList.add("kanban-col-drag"); };
  const onDragLeave = (e) => e.currentTarget.classList.remove("kanban-col-drag");

  return (
    <div className="p-8 lg:p-12">
      <PageHeader
        eyebrow="06 / Pipeline"
        title="Tasks & Approvals"
        subtitle="Every reel-in-flight. Assign, track, review — switch between board and table views without losing state."
        actions={
          <div className="flex items-center gap-3">
            <div className="rounded-full border border-white/10 p-0.5 bg-zinc-900 flex">
              <button
                data-testid="view-kanban-btn"
                onClick={() => setView("kanban")}
                className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 ${
                  view === "kanban" ? "bg-white text-zinc-950 font-semibold" : "text-zinc-400"
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Kanban
              </button>
              <button
                data-testid="view-table-btn"
                onClick={() => setView("table")}
                className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 ${
                  view === "table" ? "bg-white text-zinc-950 font-semibold" : "text-zinc-400"
                }`}
              >
                <TableIcon className="h-3.5 w-3.5" /> Table
              </button>
            </div>
            {effectiveRole === "admin" && (
              <Button
                data-testid="open-assign-task-btn"
                onClick={() => setOpenAdd(true)}
                className="rounded-full bg-white text-zinc-950 hover:bg-zinc-200"
              >
                <Plus className="h-4 w-4 mr-2" /> Assign Task
              </Button>
            )}
          </div>
        }
      />

      {view === "table" && (
        <div className="mb-4 flex items-center gap-2">
          {["all", ...COLUMNS].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full border ${
                filter === s
                  ? "bg-white text-zinc-950 border-white"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
      )}

      {view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {COLUMNS.map((col) => (
            <div
              key={col}
              data-testid={`kanban-col-${col.toLowerCase().replace(/\s/g, "-")}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, col)}
              className="rounded-xl border border-white/5 bg-zinc-900/30 p-4 min-h-[300px]"
              style={{ transition: "background-color 0.2s ease" }}
            >
              <div className="flex items-center justify-between mb-4">
                <StatusBadge value={col} />
                <span className="font-mono text-xs text-zinc-500">
                  {tasks.filter((t) => t.status === col).length}
                </span>
              </div>
              <div className="space-y-3">
                {tasks
                  .filter((t) => t.status === col)
                  .map((t) => (
                    <div
                      key={t.id}
                      data-testid={`task-card-${t.id}`}
                      draggable={effectiveRole === "admin"}
                      onDragStart={(e) => onDragStart(e, t.id)}
                      onClick={() => setOpenDetail(t)}
                      className="cursor-pointer rounded-lg border border-white/10 bg-zinc-900 p-4 hover:border-white/20"
                      style={{ transition: "border-color 0.2s ease" }}
                    >
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                        {cliName(t.client_id)}
                      </div>
                      <div className="mt-1 font-medium text-sm leading-tight">{t.title}</div>
                      <div className="mt-3 flex items-center justify-between">
                        <StatusBadge value={t.priority} showDot={false} />
                        <span className="text-[10px] font-mono text-zinc-500">{t.deadline}</span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
                        {edPic(t.editor_id) ? (
                          <img src={edPic(t.editor_id)} alt="" className="h-6 w-6 rounded-full object-cover" />
                        ) : (
                          <div className="h-6 w-6 rounded-full bg-zinc-800" />
                        )}
                        <span className="text-xs text-zinc-400 truncate">{edName(t.editor_id)}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-zinc-900 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-white/5">
                <th className="p-4 pl-6">Title</th>
                <th className="p-4">Client</th>
                <th className="p-4">Editor</th>
                <th className="p-4">Priority</th>
                <th className="p-4">Deadline</th>
                <th className="p-4">Status</th>
                <th className="p-4 pr-6"></th>
              </tr>
            </thead>
            <tbody data-testid="tasks-table-body">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-zinc-500 text-sm">
                    No tasks.
                  </td>
                </tr>
              )}
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setOpenDetail(t)}
                  className="border-b border-white/5 last:border-0 hover:bg-zinc-900/60 cursor-pointer"
                >
                  <td className="p-4 pl-6 font-medium">{t.title}</td>
                  <td className="p-4 text-sm text-zinc-300">{cliName(t.client_id)}</td>
                  <td className="p-4 text-sm text-zinc-300">{edName(t.editor_id)}</td>
                  <td className="p-4"><StatusBadge value={t.priority} showDot={false} /></td>
                  <td className="p-4 font-mono text-xs text-zinc-400">{t.deadline}</td>
                  <td className="p-4"><StatusBadge value={t.status} /></td>
                  <td className="p-4 pr-6 text-right">
                    <ExternalLink className="h-4 w-4 text-zinc-500 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign task */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent data-testid="assign-task-dialog" className="bg-zinc-950 border-white/10 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Assign new edit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="task-client-select" className="mt-1.5 bg-zinc-900 border-white/10">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Editor</Label>
                <Select value={form.editor_id} onValueChange={(v) => setForm({ ...form, editor_id: v })}>
                  <SelectTrigger data-testid="task-editor-select" className="mt-1.5 bg-zinc-900 border-white/10">
                    <SelectValue placeholder="Assign editor" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                    {editors.map((e) => (
                      <SelectItem key={e.user_id} value={e.user_id}>
                        {e.name} · {e.active_tasks_count} active
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Script / Reel title</Label>
              <Input
                data-testid="task-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1.5 bg-zinc-900 border-white/10"
                placeholder="Instagram Reel — Product Reveal"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Deadline</Label>
                <Input
                  data-testid="task-deadline-input"
                  type="date"
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  className="mt-1.5 bg-zinc-900 border-white/10"
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="task-priority-select" className="mt-1.5 bg-zinc-900 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                    {["Low", "Medium", "High"].map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Raw footage Drive folder (optional — defaults to client's 01_Raw_Footage)</Label>
              <Input
                data-testid="task-raw-input"
                value={form.raw_asset_url}
                onChange={(e) => setForm({ ...form, raw_asset_url: e.target.value })}
                className="mt-1.5 bg-zinc-900 border-white/10 font-mono text-xs"
                placeholder="https://drive.google.com/..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenAdd(false)}>Cancel</Button>
            <Button
              data-testid="submit-task-btn"
              onClick={submitAdd}
              disabled={!form.client_id || !form.editor_id || !form.title || !form.deadline}
              className="bg-white text-zinc-950 hover:bg-zinc-200 rounded-full"
            >
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / Approve */}
      <Dialog open={!!openDetail} onOpenChange={(v) => { if (!v) { setOpenDetail(null); setFeedback(""); } }}>
        <DialogContent data-testid="task-detail-dialog" className="bg-zinc-950 border-white/10 text-zinc-100 max-w-lg">
          {openDetail && (
            <>
              <DialogHeader>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {cliName(openDetail.client_id)}
                </div>
                <DialogTitle className="font-display text-2xl mt-1">{openDetail.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <StatusBadge value={openDetail.status} />
                  <StatusBadge value={openDetail.priority} showDot={false} />
                  <span className="text-xs text-zinc-500 font-mono">Due {openDetail.deadline}</span>
                </div>
                <div className="rounded-lg border border-white/5 p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Assigned to
                  </div>
                  <div>{edName(openDetail.editor_id)}</div>
                </div>
                {openDetail.raw_asset_url && (
                  <a
                    href={openDetail.raw_asset_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate rounded-lg border border-white/5 p-3 hover:border-white/10"
                  >
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                      Raw footage
                    </div>
                    <div className="text-emerald-400 font-mono text-xs truncate">
                      {openDetail.raw_asset_url}
                    </div>
                  </a>
                )}
                {openDetail.final_output_url && (
                  <a
                    href={openDetail.final_output_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate rounded-lg border border-white/5 p-3 hover:border-white/10"
                  >
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                      Final output
                    </div>
                    <div className="text-sky-400 font-mono text-xs truncate">
                      {openDetail.final_output_url}
                    </div>
                  </a>
                )}
                {openDetail.feedback_notes && (
                  <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-rose-300 mb-1">
                      Feedback
                    </div>
                    <div className="text-sm text-zinc-200">{openDetail.feedback_notes}</div>
                  </div>
                )}

                {effectiveRole === "admin" && openDetail.status === "Review" && (
                  <div className="space-y-3 pt-2">
                    <Textarea
                      data-testid="feedback-input"
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Feedback notes (required only for Request Changes)"
                      className="bg-zinc-900 border-white/10"
                      rows={3}
                    />
                    <div className="flex gap-2">
                      <Button
                        data-testid="approve-task-btn"
                        onClick={() => approve(openDetail.id)}
                        className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-full"
                      >
                        <Check className="h-4 w-4 mr-2" /> Approve
                      </Button>
                      <Button
                        data-testid="request-changes-btn"
                        onClick={() => requestChanges(openDetail.id)}
                        variant="outline"
                        className="flex-1 border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded-full"
                      >
                        <X className="h-4 w-4 mr-2" /> Request Changes
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
